import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import Button from '~/components/core/Atomic/Buttons/Button';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { CustomField } from '~/components/IncidentConfiguration/ConfiguredFields';
import { CustomFieldsContextProvider } from '~/components/IncidentConfiguration/CustomFieldsContext';
import { isClaimPolicyManuallyFilled, isCountryWithStates, isMarshmallowPolicy, reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';
import { getLocaleRegion } from '~/Utils/regionUtils';

import { isoDateToUs, timeToLocalTime } from '../../../../DateTimeUtils';
import {
  getColorForPolicyStatus,
  getPolicyNumberPrefix,
  getPolicyStatus,
  getPolicyStatusConsiderTimeRanges,
} from '../../../../PolicyUtils';
import { COUNTRIES_DICT } from '../../../../Types';
import { getLobIcon } from '../../../../Utils/lobUtils';
import CardDialog from '../../../CardDialog';
import { ContactShowOnlyTextField } from '../../../ContactTextFieldFormik';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from '../../../core';
import useCurrencyFormatter from '../../../CurrencyFormatterContext';
import { FilesIcon, PencilIcon } from '../../../icons';
import InlineIconButton from '../../../InlineIconButton';
import { ShowOnlyTextField } from '../../../TextFieldFormik';

import EditPolicyDialog from './EditPolicyDialog/EditPolicyDialog';
import { getBrokerFieldConfig } from './brokerFieldConfig';
import NamedDriversWrapper from './NamedDriversWrapper';
import PolicyCustomFields from './PolicyCustomFields';

import { useStyles } from '../../../../assets/styles';

function PolicyDetailsCard(props) {
  const { classes } = useStyles();
  const [openEditPolicyDialog, setOpenEditPolicyDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { claim, onClaimUpdate, numColumns } = props;
  const effectiveColumns = numColumns ?? 3;
  const { policy } = claim;
  const { configured_fields_values: configuredFieldsValues = {} } = policy;
  const { incidentConfiguration } = useIncidentConfiguration();

  const title = 'Policy Details';
  const { currencyFormatter } = useCurrencyFormatter();
  const { lobConfigurationsDict = {} } = useLobConfiguration();
  const policyLobIcon = getLobIcon({ lob: policy.lob.replace('_policy', '_claim'), lobConfigurationsDict });

  const isManualPolicy = isClaimPolicyManuallyFilled(claim);
  const policyURLSuffix = isManualPolicy ? 'manual_policy' : 'api_policy';
  const configuredFieldsConfiguration = incidentConfiguration?.policy?.configured_fields ?? [];
  const brokerConfig = getBrokerFieldConfig(incidentConfiguration);

  let documentUrl = policy.policy_document_url;
  if (documentUrl && !documentUrl.startsWith('http')) {
    documentUrl = 'https://' + documentUrl;
    try {
      new URL(documentUrl);
    } catch (err) {
      documentUrl = null;
    }
  }

  const action = (
    <div>
      <div className="mr-8 flex items-center">
        <Tooltip title={policy.policy_document_url ? 'Policy Document' : 'Document link was not supplied'}>
          <span>
            <Button
              className="bg-transparent font-semibold normal-case text-slate-900 underline hover:bg-transparent hover:text-teal-700 hover:underline hover:transition-none disabled:text-slate-700"
              disabled={!policy.policy_document_url}
              onClick={() => documentUrl && window.open(documentUrl, '_blank')}
              startIcon={<FilesIcon className={classes.hoverableNonStrokedIcon} />}
            >
              Policy Document
            </Button>
          </span>
        </Tooltip>
        <div className="w-1 mx-[10px] h-[25px] bg-slate-600" />
        <RestrictedPermissions
          action={isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY}
          verb={PERMISSION_VERBS.WRITE}
        >
          <InlineIconButton
            icon={PencilIcon}
            className="cursor-pointer hover:stroke-teal-700"
            onClick={() => setOpenEditPolicyDialog(true)}
            tooltipTitle="Edit Policy"
          />
        </RestrictedPermissions>
      </div>
    </div>
  );

  let policyStatus = policy.policy_status;
  if (policyStatus !== 'void') {
    const policyStatusDuringLoss = getPolicyStatusConsiderTimeRanges({
      policy,
      date_of_loss: claim?.incident?.date_of_loss,
      time_of_loss: claim?.incident?.time_of_loss,
      reported_date: claim?.reported_date,
    });
    policyStatus = getPolicyStatus(policyStatusDuringLoss, true);
  }

  let cancellationDate = '';
  if (policy.is_cancelled) {
    if (policy.cancelled_date) {
      cancellationDate += isoDateToUs(policy.cancelled_date);
      if (policy.cancelled_time) {
        cancellationDate += ' ' + timeToLocalTime(policy.cancelled_time);
      }
    }
  }

  async function handlePolicyUpdate(values) {
    setIsSaving(true);
    try {
      const url = `/api/v1/claims/${claim.id}/${policyURLSuffix}`;
      await axios.patch(url, values);
      await onClaimUpdate();
      setOpenEditPolicyDialog(false);
    } catch (error) {
      reportAxiosError(error);
      setIsSaving(false);
      throw error;
    }
    setIsSaving(false);
  }

  return (
    <>
      <CardDialog
        title={title}
        action={action}
        headerStyle={{ padding: '0 0 24px' }}
        containerStyle={{ padding: '24px' }}
        contentStyle={{ padding: '0' }}
      >
        <div className={cn('mb-32 grid gap-x-12 gap-y-32', effectiveColumns === 4 ? 'grid-cols-4' : 'grid-cols-3')}>
          <ShowOnlyTextField
            classes={classes}
            label={
              <span className="flex">
                <span className="mr-12">{policyLobIcon}</span>
                {getPolicyNumberPrefix(policy.lob, lobConfigurationsDict) + ' '}
                Policy Number
              </span>
            }
            showOnlyValueComponent={
              <span className="pl-[28px] font-semibold text-pink-600">{policy.policy_number}</span>
            }
          />
          <ShowOnlyTextField
            classes={classes}
            label="Status"
            showOnlyValueComponent={
              <span className="font-semibold capitalize" style={{ color: getColorForPolicyStatus(policyStatus) }}>
                {policyStatus}
              </span>
            }
          />
          <ContactShowOnlyTextField
            classes={classes}
            label="Policyholder"
            contactId={policy.insured_contact_id}
            contactDisplayName={policy.insured_contact_full_name}
            shallRefetchOnContactIdChange
            // Override default 150px cap
            maxWidth="100%"
          />
          {brokerConfig?.active ? (
            <ContactShowOnlyTextField
              classes={classes}
              label={brokerConfig.desc}
              contactId={policy.broker_contact_id}
              contactDisplayName={policy.broker_contact_full_name}
              shallRefetchOnContactIdChange
              maxWidth="100%"
            />
          ) : null}
          <ShowOnlyTextField
            classes={classes}
            label="Effective Date"
            showOnlyValueComponent={
              isoDateToUs(policy.policy_effective_date) + ' ' + timeToLocalTime(policy.effective_time)
            }
          />
          <ShowOnlyTextField
            classes={classes}
            label="Expiration Date"
            showOnlyValueComponent={
              isoDateToUs(policy.policy_expiration_date) + ' ' + timeToLocalTime(policy.expiration_time)
            }
          />
          <ShowOnlyTextField classes={classes} label="Cancellation Date" showOnlyValueComponent={cancellationDate} />

          {policy?.is_claims_made ? (
            <>
              <ShowOnlyTextField classes={classes} label="Claims Made" showOnlyValueComponent="Yes" />
              <ShowOnlyTextField
                classes={classes}
                label="Retroactive Date"
                showOnlyValueComponent={
                  policy?.retroactive_date
                    ? isoDateToUs(policy?.retroactive_date) + ' ' + timeToLocalTime(policy.retroactive_time)
                    : ''
                }
              />
              <ShowOnlyTextField
                classes={classes}
                label="Days of Runoff Provision"
                showOnlyValueComponent={policy.runoff_provision}
              />
            </>
          ) : null}

          <ShowOnlyTextField classes={classes} label="Policy Type" showOnlyValueComponent={policy.policy_type} />
          {isCountryWithStates(policy.policy_country ?? getLocaleRegion()) && (
            <ShowOnlyTextField classes={classes} label="Policy State" showOnlyValueComponent={policy.policy_state} />
          )}
          <ShowOnlyTextField
            classes={classes}
            label="Policy Currency"
            showOnlyValueComponent={policy.policy_currency}
          />

          <ShowOnlyTextField
            classes={classes}
            label="Policy Country"
            showOnlyValueComponent={COUNTRIES_DICT[policy.policy_country]}
          />
          <ShowOnlyTextField
            classes={classes}
            label="Policy Limit"
            showOnlyValueComponent={policy.limit_per_policy ? currencyFormatter.format(policy.limit_per_policy) : ''}
          />
          <ShowOnlyTextField
            classes={classes}
            label="Claim Limit"
            showOnlyValueComponent={policy.limit_per_claim ? currencyFormatter.format(policy.limit_per_claim) : ''}
          />
          <ShowOnlyTextField
            classes={classes}
            label="Renewal Date"
            showOnlyValueComponent={policy.renewal_date ? isoDateToUs(policy.renewal_date) : ''}
          />
          <ShowOnlyTextField
            classes={classes}
            label="Last Modified Date"
            showOnlyValueComponent={policy.last_update_date ? isoDateToUs(policy.last_update_date) : ''}
          />
          <CustomFieldsContextProvider customFieldsConfigurations={configuredFieldsConfiguration}>
            {configuredFieldsConfiguration
              ?.filter((field) => !(field?.active === false))
              ?.map((field) => (
                <CustomField
                  key={field.id}
                  field={field}
                  readOnly
                  disabled
                  value={configuredFieldsValues[field.id]}
                  inline={false}
                  idPrefix=""
                  overFlowWithTooltip
                  // Truncate at the grid column width (not a fixed 100px) so the value uses the
                  // available space and only ellipsizes when it actually overflows the column.
                  maxWidth="100%"
                />
              ))}
          </CustomFieldsContextProvider>
        </div>

        {(policy.custom_fields?.length > 0 || policy.underwriting_company || isMarshmallowPolicy(policy)) && (
          <PolicyCustomFields customFields={policy.custom_fields ?? []} policy={policy} />
        )}

        {policy.lob === 'auto_policy' && (
          <NamedDriversWrapper claim={claim} onClaimUpdate={onClaimUpdate} namedDrivers={policy.named_drivers} />
        )}
      </CardDialog>

      {openEditPolicyDialog && (
        <EditPolicyDialog
          onSaveClick={(values) => handlePolicyUpdate(values)}
          onClose={() => !isSaving && setOpenEditPolicyDialog(false)}
          isSaving={isSaving}
          policyStatus={policyStatus}
          policy={policy}
          lob={policy.lob}
          claim={claim}
        />
      )}
    </>
  );
}

PolicyDetailsCard.propTypes = {
  claim: PropTypes.object.isRequired,
  onClaimUpdate: PropTypes.func.isRequired,
  numColumns: PropTypes.oneOf([2, 3, 4, 6]),
};

export default PolicyDetailsCard;
